import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const adminMetricsEnvSchema = z.enum(["staging", "prod-beta", "all"]);
export const adminMetricsIntervalSchema = z.enum(["1m", "5m", "15m", "1h"]);
export const adminMetricsSourceStatusSchema = z.enum([
  "ok",
  "degraded",
  "disabled",
]);
export const adminMetricsSeveritySchema = z.enum(["warning", "critical"]);
export const adminMetricsStatusSchema = z.enum([
  "ok",
  "warning",
  "critical",
  "no_data",
]);

export const adminMetricsQuerySchema = z.object({
  env: adminMetricsEnvSchema.default("all"),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  interval: adminMetricsIntervalSchema.default("5m"),
  host: z.string().min(1).optional(),
  service: z.string().min(1).optional(),
});

export const adminMetricsSampleSchema = z.object({
  timestamp: z.number().int(),
  value: z.number(),
});

export const adminInfraSummaryItemSchema = z.object({
  env: z.enum(["staging", "prod-beta"]),
  status: adminMetricsStatusSchema,
  source_status: adminMetricsSourceStatusSchema,
  cpu_percent: z.number().nullable(),
  memory_percent: z.number().nullable(),
  memory_used_bytes: z.number().nullable(),
  disk_used_percent: z.number().nullable(),
  load1: z.number().nullable(),
  network_rx_bps: z.number().nullable(),
  network_tx_bps: z.number().nullable(),
  container_restarts_10m: z.number().nullable(),
  issues: z.array(z.string()),
  updated_at: z.string().datetime(),
});

export const adminInfraSummarySuccessSchema = z.object({
  source_status: adminMetricsSourceStatusSchema,
  query: adminMetricsQuerySchema,
  environments: z.array(adminInfraSummaryItemSchema),
});

export const adminInfraMetricSeriesSchema = z.object({
  env: z.enum(["staging", "prod-beta"]).optional(),
  host: z.string().nullable().optional(),
  service: z.string().nullable().optional(),
  samples: z.array(adminMetricsSampleSchema),
});

export const adminInfraMetricSchema = z.object({
  metric: z.string().min(1),
  unit: z.enum(["percent", "bytes", "bps", "count", "load"]),
  series: z.array(adminInfraMetricSeriesSchema),
});

export const adminInfraTimeseriesSuccessSchema = z.object({
  source_status: adminMetricsSourceStatusSchema,
  query: adminMetricsQuerySchema,
  metrics: z.array(adminInfraMetricSchema),
  warnings: z.array(z.string()).optional(),
});

export const adminInfraIncidentSchema = z.object({
  id: z.string().min(1),
  env: z.enum(["staging", "prod-beta"]),
  metric: z.string().min(1),
  severity: adminMetricsSeveritySchema,
  threshold: z.number(),
  value: z.number(),
  timestamp: z.number().int(),
});

export const adminInfraIncidentsSuccessSchema = z.object({
  source_status: adminMetricsSourceStatusSchema,
  query: adminMetricsQuerySchema,
  incidents: z.array(adminInfraIncidentSchema),
});

export const adminMetricsCorrelationBucketSchema = z.object({
  timestamp: z.number().int(),
  env: z.enum(["staging", "prod-beta"]),
  infra_incident_count: z.number().int().nonnegative(),
  failed_tool_calls: z.number().int().nonnegative(),
  terminal_timeouts: z.number().int().nonnegative(),
  classification: z.enum([
    "healthy",
    "infra_correlated",
    "app_only",
    "unknown",
  ]),
});

export const adminMetricsCorrelationSuccessSchema = z.object({
  source_status: adminMetricsSourceStatusSchema,
  query: adminMetricsQuerySchema,
  buckets: z.array(adminMetricsCorrelationBucketSchema),
});

function authHeaders(accessToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

function appendQuery(url: URL, query: z.infer<typeof adminMetricsQuerySchema>) {
  url.searchParams.set("env", query.env);
  url.searchParams.set("interval", query.interval);
  if (query.from) url.searchParams.set("from", query.from);
  if (query.to) url.searchParams.set("to", query.to);
  if (query.host) url.searchParams.set("host", query.host);
  if (query.service) url.searchParams.set("service", query.service);
}

export function adminInfraSummaryPath(): string {
  return "/api/admin/metrics/infra/summary";
}

export function adminInfraTimeseriesPath(): string {
  return "/api/admin/metrics/infra/timeseries";
}

export function adminInfraIncidentsPath(): string {
  return "/api/admin/metrics/infra/incidents";
}

export function adminMetricsCorrelationPath(): string {
  return "/api/admin/metrics/correlation";
}

async function fetchTypedResponse<T>({
  apiBaseUrl,
  accessToken,
  path,
  query,
  responseSchema,
  fetchImpl,
  errorMessage,
}: {
  apiBaseUrl: string;
  accessToken: string;
  path: string;
  query: z.input<typeof adminMetricsQuerySchema>;
  responseSchema: z.ZodSchema<T>;
  fetchImpl?: FetchLike;
  errorMessage: string;
}): Promise<T> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedQuery = adminMetricsQuerySchema.parse(query);
  const url = new URL(`${normalizeApiBaseUrl(apiBaseUrl)}${path}`);
  appendQuery(url, parsedQuery);

  const response = await fetchFn(url.toString(), {
    method: "GET",
    headers: authHeaders(accessToken),
  });

  if (!response.ok) {
    throw await parseErrorResponse(response, errorMessage);
  }

  const payload = await response.json().catch(() => null);
  const parsed = responseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid admin metrics response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function fetchAdminInfraSummaryRequest({
  apiBaseUrl,
  accessToken,
  query,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  query: z.input<typeof adminMetricsQuerySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof adminInfraSummarySuccessSchema>> {
  return fetchTypedResponse({
    apiBaseUrl,
    accessToken,
    path: adminInfraSummaryPath(),
    query,
    responseSchema: adminInfraSummarySuccessSchema,
    fetchImpl,
    errorMessage: "Failed to load infra summary",
  });
}

export async function fetchAdminInfraTimeseriesRequest({
  apiBaseUrl,
  accessToken,
  query,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  query: z.input<typeof adminMetricsQuerySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof adminInfraTimeseriesSuccessSchema>> {
  return fetchTypedResponse({
    apiBaseUrl,
    accessToken,
    path: adminInfraTimeseriesPath(),
    query,
    responseSchema: adminInfraTimeseriesSuccessSchema,
    fetchImpl,
    errorMessage: "Failed to load infra timeseries",
  });
}

export async function fetchAdminInfraIncidentsRequest({
  apiBaseUrl,
  accessToken,
  query,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  query: z.input<typeof adminMetricsQuerySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof adminInfraIncidentsSuccessSchema>> {
  return fetchTypedResponse({
    apiBaseUrl,
    accessToken,
    path: adminInfraIncidentsPath(),
    query,
    responseSchema: adminInfraIncidentsSuccessSchema,
    fetchImpl,
    errorMessage: "Failed to load infra incidents",
  });
}

export async function fetchAdminMetricsCorrelationRequest({
  apiBaseUrl,
  accessToken,
  query,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  query: z.input<typeof adminMetricsQuerySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof adminMetricsCorrelationSuccessSchema>> {
  return fetchTypedResponse({
    apiBaseUrl,
    accessToken,
    path: adminMetricsCorrelationPath(),
    query,
    responseSchema: adminMetricsCorrelationSuccessSchema,
    fetchImpl,
    errorMessage: "Failed to load infra/app correlation",
  });
}
